import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../../Form/Select";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startGetContentGeneratorAdTemplates } from "../../../actions/adGenerationTemplates";
import {
  AD_TEMPLATE_KIND,
  parseContentGeneratorAdTemplatesNode,
  resolveAdTemplateKind,
  resolveAdTemplateOptionExplanation,
  resolveAdTemplateOptionLabel,
  shouldShowRecommendedAspectRatioCaption,
} from "../../../utils/adGenerationTemplates";
import {
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO,
} from "../../../utils/constants";
import {
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_EXPLANATION,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_HELPER,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_LABEL,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_LABEL_VIDEO,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_LOAD_ERROR,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_NONE,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_RECOMMENDED_RATIO,
  MODAL_CREATE_WITH_IA_AD_TEMPLATE_RECOMMENDED_RATIO_ACTION,
} from "../../../i18n/keysTranslations";
import {
  ASPECT_RATIO_SECTION_ID,
  scrollToConfigPanelSection,
} from "./utils/configPanelScroll";

const HIDDEN_AD_TEMPLATE_IDS = [
  "textPost",
  "commentThread",
  "reviewCapture",
  "searchAutocomplete",
  "checklist",
  "emailCapture",
];

const AdGenerationTemplateField = ({
  control,
  errors,
  setValue,
  watch,
  mediaType,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const kind = resolveAdTemplateKind({ mediaType });
  const [nodeTemplates, setNodeTemplates] = useState([]);
  const [templatesStatus, setTemplatesStatus] = useState("loading");
  const templateID = watch("templateID") || "";
  const aspectRatio = watch("aspectRatio") || "";
  const isLoadingTemplates = templatesStatus === "loading";

  const templates = useMemo(
    () =>
      nodeTemplates.filter(
        (template) =>
          template.kind === kind &&
          !HIDDEN_AD_TEMPLATE_IDS.includes(template.templateID)
      ),
    [nodeTemplates, kind]
  );

  const allowedAspectRatios = (
    mediaType === "video"
      ? MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO
      : MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE
  ).map((option) => option.value);

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      setTemplatesStatus("loading");
      const response = await dispatch(startGetContentGeneratorAdTemplates());
      if (cancelled) return;
      if (!response?.ok) {
        setNodeTemplates([]);
        setTemplatesStatus("error");
        return;
      }
      setNodeTemplates(
        parseContentGeneratorAdTemplatesNode({ value: response.value })
      );
      setTemplatesStatus("ready");
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (templatesStatus === "loading" || !templateID) return;
    const selected = templates.some(
      (template) => template.templateID === templateID
    );
    if (!selected) setValue("templateID", "");
  }, [templatesStatus, templateID, templates, setValue]);

  const selectedTemplate =
    templates.find((template) => template.templateID === templateID) || null;
  const labelKey =
    kind === AD_TEMPLATE_KIND.VIDEO
      ? MODAL_CREATE_WITH_IA_AD_TEMPLATE_LABEL_VIDEO
      : MODAL_CREATE_WITH_IA_AD_TEMPLATE_LABEL;
  const recommendedAspectRatio = selectedTemplate?.recommendedAspectRatio || "";
  const showRecommendedCaption = shouldShowRecommendedAspectRatioCaption({
    aspectRatio,
    recommendedAspectRatio,
    allowedAspectRatios,
  });
  const options = [
    { value: "", label: t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_NONE) },
    ...templates.map((template) => ({
      value: template.templateID,
      label: resolveAdTemplateOptionLabel({
        templateID: template.templateID,
        name: template.name,
        translate: t,
      }),
      explanation: resolveAdTemplateOptionExplanation({
        templateID: template.templateID,
        explanation: template.explanation,
        translate: t,
      }),
    })),
  ];

  const renderOptionExplanation = ({ option }) => {
    if (!option.explanation) return null;
    return (
      <Tooltip
        title={option.explanation}
        placement="right"
        PopperProps={{ style: { zIndex: 10050 } }}
        componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
      >
        <Box
          component="span"
          className="tooltip-modal"
          aria-label={t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_EXPLANATION)}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          sx={{
            display: "inline-flex",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>
    );
  };

  const onApplyRecommendedAspectRatio = () => {
    if (!showRecommendedCaption) return;
    setValue("aspectRatio", recommendedAspectRatio, {
      shouldDirty: true,
      shouldValidate: true,
    });
    scrollToConfigPanelSection({ sectionId: ASPECT_RATIO_SECTION_ID });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t(labelKey)}
        </Typography>
        {isLoadingTemplates ? <CircularProgress size={14} /> : null}
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_HELPER)}
      </Typography>
      <Select
        control={control}
        name="templateID"
        options={options}
        fullWidth
        variant="filled"
        errors={errors}
        rules={{ required: false }}
        displayEmpty
        disabled={isLoadingTemplates}
        MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
        renderTrailing={renderOptionExplanation}
      />
      {showRecommendedCaption ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_RECOMMENDED_RATIO, {
            aspectRatio: recommendedAspectRatio,
          })}{" "}
          <Box
            component="button"
            type="button"
            onClick={onApplyRecommendedAspectRatio}
            sx={{
              p: 0,
              border: 0,
              background: "none",
              color: "inherit",
              font: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_RECOMMENDED_RATIO_ACTION)}
          </Box>
        </Typography>
      ) : null}
      {templatesStatus === "error" ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t(MODAL_CREATE_WITH_IA_AD_TEMPLATE_LOAD_ERROR)}
        </Typography>
      ) : null}
    </Box>
  );
};

export default AdGenerationTemplateField;
